<!-- 
This class is intended as a reusable component for a table column header with a filter dropdown. 
Usage example:
<FilterDropdown
    label="Status"             (column name)
    :options="statusOptions"   (available options; must have fields value and label)
    :selected="selectedStatus" (current selections)
    @update:selected="selectedStatus = $event"/>
The filter icon is outlined when no filter is active, and filled when a filter is active.
-->
<template>
	<div class="header-with-filter">
		<span>{{ label }}</span>
		<div class="filter-dropdown-container">
			<imdex-btn mini text @click.stop="toggleDropdown">
				<i :class="filterIconClass()" style="font-size: 14px"></i>
			</imdex-btn>
			<div v-if="showDropdown" class="filter-dropdown" @click.stop>
				<div class="filter-options-container">
					<div class="filter-option select-all">
						<imdex-checkbox
							secondary
							:value="allSelected"
							:indeterminate="someSelected"
							@click="toggleAllSelections"
						/>
						{{ $t("usersMgmt.selectAll") }}
					</div>
					<!-- Individual Checkboxes -->
					<div v-for="option in options" :key="option.value" class="filter-option">
						<imdex-checkbox
							secondary
							style="font-size: 24px"
							:val="option.value"
							v-model:value="localSelected"
						/>
						{{ $t(option.label) }}
					</div>
				</div>
				<div class="filter-dropdown-actions">
					<imdex-btn @click.stop="clearFilters" text small secondary class="filter-btn">
						{{ $t("usersMgmt.reset") }}
					</imdex-btn>
					<imdex-btn @click.stop="applyFilters" text small secondary class="filter-btn">
						{{ $t("usersMgmt.apply") }}
						<i
							class="fal fa-greater-than"
							style="margin-left: 10px; color: inherit"
						></i>
					</imdex-btn>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	props: {
		// The column name (e.g., "Status")
		label: String,
		// The filter options.  Must contain fields value and label, e.g.
		// [ { value: "active", label: "usersMgmt.active" }, ...]
		options: Array,
		// The currently selected filter values
		selected: Array
	},
	data() {
		return {
			showDropdown: false,
			localSelected:
				this.selected?.length > 0
					? [...this.selected]
					: this.options.map((opt) => opt.value)
		};
	},
	computed: {
		allSelected: {
			get() {
				return this.localSelected.length === this.options.length;
			},
			set(value) {
				this.localSelected = value ? this.options.map((opt) => opt.value) : [];
			}
		},
		someSelected() {
			return this.localSelected.length > 0 && this.localSelected.length < this.options.length;
		}
	},
	watch: {
		selected(newSelected) {
			// Handle prop change, e.g., reset selected filter if needed
			this.localSelected =
				newSelected?.length > 0 ? [...newSelected] : this.options.map((opt) => opt.value);
		}
	},
	methods: {
		toggleDropdown() {
			this.showDropdown = !this.showDropdown;
		},
		toggleSelection(value) {
			const index = this.localSelected.indexOf(value);
			if (index === -1) {
				this.localSelected.push(value);
			} else {
				this.localSelected.splice(index, 1);
			}
		},
		toggleAllSelections() {
			if (this.localSelected.length === this.options.length) {
				this.localSelected = [];
			} else {
				this.localSelected = this.options.map((opt) => opt.value);
			}
		},
		filterIconClass() {
			return this.localSelected.length < this.options.length
				? "fas fa-filter"
				: "fal fa-filter";
		},
		clearFilters() {
			this.localSelected = [];
			this.$emit("update:selected", []); // Emit event to parent
			// close dialog on Reset as well as Apply
			this.showDropdown = false;
		},
		applyFilters() {
			if (this.allSelected) {
				this.localSelected = [];
			}
			this.$emit("update:selected", this.localSelected);
			this.showDropdown = false;
		}
	}
};
</script>

<style scoped lang="scss">
.header-with-filter {
	display: flex;
	position: relative;
	gap: 0px;
}

.filter-dropdown-container {
	display: flex;
}

.filter-options-container {
	display: flex;
	flex-direction: column;
	gap: 16px; // Spacing between options
	align-items: flex-start;
	padding: 0px;
}

/* Dropdown styling */
.filter-dropdown {
	position: absolute;
	top: 100%; // Position BELOW the column header
	background: white;
	border: 1px solid #ccc;
	z-index: 10000;
	padding: 16px;
	gap: 24px;
	border-radius: 3px;
	box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.15);
	display: flex;
	flex-direction: column;
	color: #66727c;
	font-size: 12px;
	font-weight: 500;
	line-height: 18px;

	// Override settings from the table header
	letter-spacing: normal;
	text-transform: none;
}

/* Checkbox styling */
.filter-option {
	display: flex;
	align-items: center;
	gap: 8px;
	padding-left: 16px; // indent sub-options
	cursor: pointer;
	color: $imdex-dark-grey;
}

/* Select All checkbox style */
.select-all {
	padding-left: 0px;
	display: flex;
}

/* Container for buttons at the bottom */
.filter-dropdown-actions {
	display: flex;
	justify-content: space-between;
	border-radius: 3px;
	padding: 0px;
	gap: 16px;
}

.filter-btn {
	color: $imdex-secondary;
	gap: 8px;
	padding: 12px 16px;
	font-size: 16px;
}
</style>
